import type { QueryClient } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import { notFound } from "@tanstack/react-router";
import type { UIMessage } from "ai";
import {
  ChatApiError,
  getChatHistoryApi,
  isAuthError,
} from "./hooks/use-chat-history";
import { chatHistoryKeys } from "./query-keys";

const pendingChatRouteSeeds = new Set<string>();

function getPendingChatRouteSeedKey(viewerId: string, chatId: string) {
  return `${viewerId}:${chatId}`;
}

export function chatHistoryQueryOptions({
  baseUrl,
  viewerId,
  onAuthError,
}: {
  baseUrl: string;
  viewerId: string;
  onAuthError?: () => void;
}) {
  const api = getChatHistoryApi(baseUrl);
  return queryOptions({
    queryKey: chatHistoryKeys.list(viewerId),
    queryFn: async () => {
      try {
        return await api.fetchHistory();
      } catch (error) {
        // An expired session is recoverable: hand the 401 to the caller's
        // re-auth flow and resolve with an empty list while re-authentication
        // takes over, instead of throwing to the nearest error boundary.
        if (onAuthError && isAuthError(error)) {
          onAuthError();
          return [];
        }
        throw error;
      }
    },
    staleTime: 0,
    retry: false,
  });
}

export function chatMessagesQueryOptions({
  baseUrl,
  viewerId,
  chatId,
}: {
  baseUrl: string;
  viewerId: string;
  chatId: string;
}) {
  const api = getChatHistoryApi(baseUrl);
  return queryOptions({
    queryKey: chatHistoryKeys.messages(viewerId, chatId),
    queryFn: () => api.fetchMessages(chatId),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export function seedPendingChatMessagesRoute({
  baseUrl,
  chatId,
  queryClient,
  viewerId,
}: {
  baseUrl: string;
  chatId: string;
  queryClient: QueryClient;
  viewerId: string;
}) {
  const query = chatMessagesQueryOptions({ baseUrl, viewerId, chatId });
  queryClient.setQueryData<UIMessage[]>(query.queryKey, []);
  pendingChatRouteSeeds.add(getPendingChatRouteSeedKey(viewerId, chatId));
}

export async function loadChatMessagesForRoute({
  baseUrl,
  chatId,
  queryClient,
  viewerId,
  reuseWithinMs = 0,
}: {
  baseUrl: string;
  chatId: string;
  queryClient: QueryClient;
  viewerId: string;
  /**
   * Use cached messages fetched or written this recently instead of refetching,
   * so a chat preloaded on hover opens without a second request. The cache
   * mirrors the open chat's messages, so a recent entry is the latest view.
   */
  reuseWithinMs?: number;
}) {
  const query = chatMessagesQueryOptions({ baseUrl, viewerId, chatId });
  const seedKey = getPendingChatRouteSeedKey(viewerId, chatId);

  try {
    if (
      pendingChatRouteSeeds.delete(seedKey) &&
      queryClient.getQueryData(query.queryKey) !== undefined
    ) {
      return await queryClient.ensureQueryData(query);
    }
    return await queryClient.fetchQuery({ ...query, staleTime: reuseWithinMs });
  } catch (error) {
    // A missing chat is a not-found state, not a crash: render the
    // notFoundComponent instead of surfacing it as an error.
    if (error instanceof ChatApiError && error.status === 404) {
      throw notFound();
    }
    throw error;
  }
}
